// packages/web/src/screens/sidesPresentation.tsx — B6c-2: the shared SIDES
// rendering primitives, used by BOTH the project detail (full: current +
// history + add) and the audition detail (compact: current + change flag).
// Kept in one place so the revision-colour chip + the calm change indicator
// read identically wherever sides appear.
import type { ProjectSides } from '../lib/api';
import './sidesPresentation.css';

// ---------------------------------------------------------------------------
// REVISION-COLOUR MAP. The film-industry script-revision colour sequence is a
// set of PALE PAPER stocks (white, then blue, pink, yellow, green, goldenrod,
// buff, salmon, cherry, tan, and a 'second white' when the cycle repeats). We
// render each chip in a legible approximation of its real paper colour with
// DARK ink (var(--night)) on top — every hue below is a pale pastel, so dark
// ink stays high-contrast/legible on the app's dark surface. `second_white`
// reuses white. An unknown/absent colour falls back to a neutral surface chip
// (see RevisionChip) rather than guessing a hue.
export const REVISION_COLOURS: Record<string, string> = {
  white: '#eef1f6',
  blue: '#bcd3f0',
  pink: '#f4c9d8',
  yellow: '#f0e6a8',
  green: '#bfe3c9',
  goldenrod: '#e6c86a',
  buff: '#ece3c8',
  salmon: '#f3c4a8',
  cherry: '#f0b8c0',
  tan: '#dcc9a8',
  second_white: '#eef1f6',
};

/** Prettifies a revision_colour picklist value for display ('second_white' ->
 * 'second white'). Returns null for an absent colour. */
export function revisionLabel(colour: string | null | undefined): string | null {
  if (!colour) return null;
  return colour.replace(/_/g, ' ');
}

function sidesField(sides: ProjectSides, name: string): string | null {
  const value = sides.fields[name];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** The coloured revision chip. A button when it can open its ORIGINAL file
 * (sourceId present) — otherwise a static chip. Colour is the paper stock;
 * the label is the colour name (+ version label when present). */
export function RevisionChip({
  sides,
  onViewSource,
}: {
  sides: ProjectSides;
  onViewSource: (sourceId: string) => void;
}) {
  const colour = sidesField(sides, 'revision_colour');
  const label = revisionLabel(colour) ?? 'revision';
  const versionLabel = sidesField(sides, 'version_label');
  const hue = colour ? REVISION_COLOURS[colour] : undefined;
  const text = [label, versionLabel].filter(Boolean).join(' · ');
  const style = hue ? { background: hue, color: 'var(--night)' } : undefined;

  if (sides.sourceId) {
    return (
      <button
        type="button"
        class={`rev-chip${hue ? '' : ' rev-chip--plain'}`}
        style={style}
        aria-label="Open the original sides file"
        onClick={() => onViewSource(sides.sourceId!)}
      >
        <span class="rev-chip__dot" aria-hidden="true">
          ●
        </span>
        {text}
      </button>
    );
  }
  return (
    <span class={`rev-chip rev-chip--static${hue ? '' : ' rev-chip--plain'}`} style={style}>
      <span class="rev-chip__dot" aria-hidden="true">
        ●
      </span>
      {text}
    </span>
  );
}

/** The CALM "new revision" change indicator — an FYI, never an alarm. Renders
 * only when the current revision superseded a prior one (supersedesId set). */
export function ChangeFlag({ sides }: { sides: ProjectSides }) {
  const colour = sidesField(sides, 'revision_colour');
  const label = revisionLabel(colour);
  return (
    <div class="rev-flag" role="note">
      <span class="rev-flag__mark" aria-hidden="true">
        ≈
      </span>
      New {label ? `${label} ` : ''}revision
    </div>
  );
}

/** Picks the current revision (is_current === true) from a set, falling back
 * to the most recent by received_at when none is explicitly current. */
export function currentSides(sides: ProjectSides[]): ProjectSides | null {
  const flagged = sides.find((s) => s.fields['is_current'] === true);
  if (flagged) return flagged;
  if (sides.length === 0) return null;
  return [...sides].sort((a, b) => {
    const ra = sidesField(a, 'received_at') ?? '';
    const rb = sidesField(b, 'received_at') ?? '';
    return rb.localeCompare(ra);
  })[0]!;
}

/** The prior revisions (everything that isn't the current one), most-recent
 * first — the collapsible supersedes history. */
export function priorSides(sides: ProjectSides[], current: ProjectSides | null): ProjectSides[] {
  return [...sides]
    .filter((s) => s.id !== current?.id)
    .sort((a, b) => {
      const ra = sidesField(a, 'received_at') ?? '';
      const rb = sidesField(b, 'received_at') ?? '';
      return rb.localeCompare(ra);
    });
}
